import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

/** Oglinda tipurilor din `apps/api/src/modules/acasa/tipuri.ts`. */
export type Grup = 'solicitari' | 'intarziate' | 'azi' | 'saptamana'
export type Ton = 'rosu' | 'galben' | 'neutru'
export type Iconita =
  | 'aproba'
  | 'cutie'
  | 'bani'
  | 'camion'
  | 'task'
  | 'tel'
  | 'doc'
  | 'alerta'
  | 'ceas'
  | 'mail'
  | 'bon'
  | 'om'
  | 'stand'

export interface Rand {
  id: string
  grup: Grup
  ton: Ton
  icon: Iconita
  titlu: string
  detaliu?: string
  semnal?: string
  link: string
  buton: string
}

export interface RaspunsRanduri {
  randuri: Rand[]
  /** Surse care n-au putut fi citite — pagina nu spune „totul la zi" peste ele. */
  neverificate: string[]
}

export interface Cifra {
  id: string
  eticheta: string
  valoare: number
  unitate: 'buc' | 'lei'
  comparatie?: string
  sens?: 'sus' | 'jos'
  inainte?: number
  serie?: number[]
  link: string
}

export interface SursaDate {
  id: string
  nume: string
  la: string | null
  pragOre: number
}

export interface Notificare {
  id: number
  /** Felul — dupa el se vede ce e solicitare (`esteSolicitare`). */
  kind: string
  title: string
  body: string | null
  link: string | null
  readAt: string | null
  createdAt: string
}

/*
  Fiecare sectiune se incarca separat: un sync lent (tabloul de vanzari citeste din EA) nu tine
  pagina in loc. Se reimprospateaza la 2 minute si la intoarcerea in fereastra.
*/
const PROASPAT = { refetchInterval: 120_000, staleTime: 60_000 }

export const useDeRezolvat = () =>
  useQuery<RaspunsRanduri>({
    queryKey: ['acasa', 'de-rezolvat'],
    queryFn: async () => (await api.get('/acasa/de-rezolvat')).data,
    ...PROASPAT,
  })

export const useScadente = () =>
  useQuery<RaspunsRanduri>({
    queryKey: ['acasa', 'scadente'],
    // Tabloul de vanzari poate dura (EA); serverul are limita lui de 30 de secunde.
    queryFn: async () => (await api.get('/acasa/scadente', { timeout: 45_000 })).data,
    ...PROASPAT,
  })

export const useCifre = () =>
  useQuery<{ cifre: Cifra[]; implicite: string[]; neverificate: string[] }>({
    queryKey: ['acasa', 'cifre'],
    queryFn: async () => (await api.get('/acasa/cifre')).data,
    ...PROASPAT,
  })

export const useSurseDate = () =>
  useQuery<{ surse: SursaDate[] }>({
    queryKey: ['acasa', 'surse-date'],
    queryFn: async () => (await api.get('/acasa/surse-date')).data,
    ...PROASPAT,
  })

export const useNoutati = () =>
  useQuery<{ notifications: Notificare[] }>({
    queryKey: ['acasa', 'noutati'],
    queryFn: async () => (await api.get('/notifications')).data,
    ...PROASPAT,
  })

/*
  Ce tine minte browserul — preferinte ale omului, nu date: ce sectiuni a restrans, ce cifre a
  ales, ce randuri a amanat pana maine. Totul cu try/catch: fara stocare, pagina merge oricum.
*/
export function citeste<T>(cheie: string, implicit: T): T {
  try {
    const v = localStorage.getItem(cheie)
    return v == null ? implicit : (JSON.parse(v) as T)
  } catch {
    return implicit
  }
}
export function scrie(cheie: string, v: unknown): void {
  try {
    localStorage.setItem(cheie, JSON.stringify(v))
  } catch {
    /* fara stocare: preferinta tine pana la reincarcare */
  }
}

/** Ziua de azi, local — cheia pentru „Mâine" (un rand amanat reapare a doua zi). */
export function ziLocala(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export const lei = (n: number) =>
  `${n.toLocaleString('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} lei`
